import dayjs from 'dayjs';
import i18n from 'i18next';
import { Icons, Status } from 'plume-ui';
import { Heading, SortDirection } from 'plume-ui/dist/components/Table/Table';
import { DataRow } from 'plume-ui/src/components/Table/Table';
import { STANDARD_DATE_TIME_FORMAT } from '../../../../config';
import { isRhoOrCiSignal } from '../../../../utils/environments';

const outreachEventsTableColorCode = (status: string): string => {
  if (status === 'Cleared') return '#17E3AE';
  return '#FA1478';
};

const outreachEventLocationsTableColorCode = (status: string): string => {
  if (status === 'Resolved') return '#17E3AE';
  return '#FA1478';
};

export const outreachEventsTableHeaders = (
  openModalFunction: (id: string, locations: number) => void,
): Heading[] => [
  {
    name: i18n.t('monitors.monitorInfo.monitorEventTable.outreachEventName'),
    fieldName: 'outreachEvent',
    render: (rowData: DataRow) => (
      <div
        onClick={() =>
          openModalFunction(
            rowData.outreachEvent.toString(),
            +rowData.affectedLocations,
          )
        }
        className="MonitorInfo__table__link"
      >
        {rowData.outreachEvent.toString()}
      </div>
    ),
  },
  {
    name: i18n.t('monitors.monitorInfo.monitorEventTable.startTime'),
    fieldName: 'startTime',
    sortFnGen: (sortDir: SortDirection) => {
      return (a, b) => {
        if (a.startTime === '') return 1;
        if (b.startTime === '') return -1;
        const compareDate =
          dayjs(a.startTime, STANDARD_DATE_TIME_FORMAT).valueOf() -
          dayjs(b.startTime, STANDARD_DATE_TIME_FORMAT).valueOf();
        if (compareDate === 0) return 0;
        return compareDate < 0 ? sortDir : -sortDir;
      };
    },
  },
  {
    name: i18n.t('monitors.monitorInfo.monitorEventTable.endTime'),
    fieldName: 'endTime',
    sortFnGen: (sortDir: SortDirection) => {
      return (a, b) => {
        if (a.endTime === '') return 1;
        if (b.endTime === '') return -1;
        const compareDate =
          dayjs(a.endTime, STANDARD_DATE_TIME_FORMAT).valueOf() -
          dayjs(b.endTime, STANDARD_DATE_TIME_FORMAT).valueOf();
        if (compareDate === 0) return 0;
        return compareDate < 0 ? sortDir : -sortDir;
      };
    },
  },
  {
    name: i18n.t('monitors.monitorInfo.monitorEventTable.eventStatus'),
    fieldName: 'eventStatus',
    render: (rowData: DataRow) => (
      <Status
        color={outreachEventsTableColorCode(rowData.eventStatus)}
        label={rowData.eventStatus}
      />
    ),
    sortFnGen: (sortDir) => (a, b) => {
      if (a.eventStatus === b.eventStatus) return 0;
      return a.eventStatus > b.eventStatus ? -sortDir : sortDir;
    },
  },
  {
    name: i18n.t(
      'monitors.monitorInfo.monitorEventTable.totalAffectedLocations',
    ),
    fieldName: 'affectedLocations',
    sortFnGen: (sortDir) => (a, b) => {
      if (+a.affectedLocations === +b.affectedLocations) return 0;
      return +a.affectedLocations > +b.affectedLocations ? -sortDir : sortDir;
    },
  },
  {
    name: i18n.t('monitors.monitorInfo.monitorEventTable.unresolvedLocations'),
    fieldName: 'unresolvedLocations',
    sortFnGen: (sortDir) => (a, b) => {
      if (+a.unresolvedLocations === +b.unresolvedLocations) return 0;
      return +a.unresolvedLocations > +b.unresolvedLocations
        ? -sortDir
        : sortDir;
    },
  },
  {
    name: i18n.t('monitors.monitorInfo.monitorEventTable.targetAudience'),
    fieldName: 'targetAudience',
  },
  {
    name: i18n.t('monitors.monitorInfo.monitorEventTable.emailSent'),
    fieldName: 'emailSent',
    sortFnGen: (sortDir) => (a, b) => {
      if (a.emailSent === '') return 1;
      if (b.emailSent === '') return -1;
      const compareDate =
        dayjs(b.emailSent, STANDARD_DATE_TIME_FORMAT).valueOf() -
        dayjs(a.emailSent, STANDARD_DATE_TIME_FORMAT).valueOf();
      if (compareDate === 0) return 0;
      return sortDir === 1 ? compareDate : -compareDate;
    },
  },
  {
    name: i18n.t(
      'monitors.monitorInfo.monitorEventTable.pushNotificationSentCount',
    ),
    fieldName: 'pushNotificationSentCount',
    sortFnGen: (sortDir) => (a, b) => {
      if (+a.pushNotificationSentCount === +b.pushNotificationSentCount)
        return 0;
      return +a.pushNotificationSentCount > +b.pushNotificationSentCount
        ? -sortDir
        : sortDir;
    },
  },
  {
    name: i18n.t(
      'monitors.monitorInfo.monitorEventTable.pushNotificationReceivedCount',
    ),
    fieldName: 'pushNotificationReceivedCount',
    sortFnGen: (sortDir) => (a, b) => {
      if (+a.pushNotificationReceivedCount === +b.pushNotificationReceivedCount)
        return 0;
      return +a.pushNotificationReceivedCount > +b.pushNotificationReceivedCount
        ? -sortDir
        : sortDir;
    },
  },
  {
    name: i18n.t(
      'monitors.monitorInfo.monitorEventTable.pushNotificationClickedCount',
    ),
    fieldName: 'pushNotificationClickedCount',
    sortFnGen: (sortDir) => (a, b) => {
      if (+a.pushNotificationClickedCount === +b.pushNotificationClickedCount)
        return 0;
      return +a.pushNotificationClickedCount > +b.pushNotificationClickedCount
        ? -sortDir
        : sortDir;
    },
  },
  {
    name: i18n.t(
      'monitors.monitorInfo.monitorEventTable.pushNotificationErrorCount',
    ),
    fieldName: 'pushNotificationBlockErrorCount',
    sortFnGen: (sortDir) => (a, b) => {
      if (
        +a.pushNotificationBlockErrorCount ===
        +b.pushNotificationBlockErrorCount
      )
        return 0;
      return +a.pushNotificationBlockErrorCount >
        +b.pushNotificationBlockErrorCount
        ? -sortDir
        : sortDir;
    },
  },
  {
    name: i18n.t('monitors.monitorInfo.monitorEventTable.webhookCount'),
    fieldName: 'webhookCount',
    sortFnGen: (sortDir) => (a, b) => {
      if (+a.webhookCount === +b.webhookCount) return 0;
      return +a.webhookCount > +b.webhookCount ? -sortDir : sortDir;
    },
  },
  {
    name: i18n.t('monitors.monitorInfo.monitorEventTable.webhookErrorCount'),
    fieldName: 'webhookErrorCount',
    sortFnGen: (sortDir) => (a, b) => {
      if (+a.webhookErrorCount === +b.webhookErrorCount) return 0;
      return +a.webhookErrorCount > +b.webhookErrorCount ? -sortDir : sortDir;
    },
  },
];

export const getOutreachEventLocationsTableHeaders = (): Heading[] => [
  {
    name: i18n.t('monitors.monitorInfo.monitorEventLocationTable.locationId'),
    fieldName: 'locationId',
    render: (rowData: DataRow) => (
      <span className="TriggeredOutreachModal__table__locationIdCell">
        <span>{rowData.locationId}</span>
        <Icons.CopyIcon
          width={16}
          className="TriggeredOutreachModal__table__locationIdCell__icon"
          onClick={async () =>
            await navigator.clipboard.writeText(rowData.locationId)
          }
        />
      </span>
    ),
  },
  {
    name: i18n.t('monitors.monitorInfo.monitorEventLocationTable.status'),
    fieldName: 'status',
    render: (rowData: DataRow) => (
      <Status
        color={outreachEventLocationsTableColorCode(rowData.eventStatus)}
        label={rowData.eventStatus}
      />
    ),
  },
  {
    name: i18n.t('monitors.monitorInfo.monitorEventLocationTable.startTime'),
    fieldName: 'startTime',
    render: (rowData: DataRow) => (
      <div className="Table__text-cell">{rowData.startTime}</div>
    ),
  },
  {
    name: i18n.t('monitors.monitorInfo.monitorEventLocationTable.endTime'),
    fieldName: 'endTime',
    render: (rowData: DataRow) => (
      <div className="Table__text-cell">{rowData.endTime}</div>
    ),
  },
  {
    name: i18n.t('monitors.monitorInfo.monitorEventLocationTable.duration'),
    fieldName: 'duration',
    render: (rowData: DataRow) => (
      <div className="Table__text-cell">{rowData.duration}</div>
    ),
  },
  {
    name: i18n.t('monitors.monitorInfo.monitorEventLocationTable.sentTime'),
    fieldName: 'sentTime',
    render: (rowData: DataRow) => (
      <div className="Table__text-cell">{rowData.sentTime}</div>
    ),
  },
  {
    name: i18n.t('monitors.monitorInfo.monitorEventLocationTable.action'),
    fieldName: 'action',
    render: (rowData: DataRow) => (
      <a href={rowData.action} target="_blank" rel="noreferrer">
        {i18n.t('frontline')}
      </a>
    ),
  },
];

const plumeLocationsCsvHeaders = [
  {
    label: i18n.t('monitors.monitorInfo.monitorEventLocationTable.locationId'),
    key: 'location_id',
  },
  {
    label: i18n.t('monitors.monitorInfo.monitorEventLocationTable.customerId'),
    key: 'customer_id',
  },
  {
    label: i18n.t('monitors.monitorInfo.monitorEventLocationTable.startTime'),
    key: 'created_at',
  },
  {
    label: i18n.t('monitors.monitorInfo.monitorEventLocationTable.endTime'),
    key: 'cleared_at',
  },
  {
    label: i18n.t('monitors.monitorInfo.monitorEventLocationTable.sentTime'),
    key: 'sent_at',
  },
];

const rhoLocationsCsvHeaders = [
  {
    label: i18n.t('monitors.monitorInfo.monitorEventLocationTable.accountId'),
    key: 'account_id',
  },
];

export const locationsCSVHeaders = () => {
  const res = [];
  if (isRhoOrCiSignal()) {
    res.push(...rhoLocationsCsvHeaders);
  }
  res.push(...plumeLocationsCsvHeaders);
  return res;
};

export enum MonitorCriteriaEnum {
  specificIssue = 'specificIssue',
  issueCategory = 'issueCategory',
  healthScore = 'healthScore',
  includeList = 'includeList',
  excludeList = 'excludeList',
}

export const subscriberStatFixtures = [
  {
    label: i18n.t('monitors.monitorInfo.subscriberStats.sent.label'),
    info: i18n.t('monitors.monitorInfo.subscriberStats.sent.info'),
  },
  {
    label: i18n.t('monitors.monitorInfo.subscriberStats.received.label'),
    info: i18n.t('monitors.monitorInfo.subscriberStats.received.info'),
  },
  {
    label: i18n.t('monitors.monitorInfo.subscriberStats.clicked.label'),
    info: i18n.t('monitors.monitorInfo.subscriberStats.clicked.info'),
  },
  {
    label: i18n.t('monitors.monitorInfo.subscriberStats.blockedError.label'),
    info: i18n.t('monitors.monitorInfo.subscriberStats.blockedError.info'),
  },
];

export const tooltipPayloadMock = [
  {
    stroke: '#B17395',
    strokeWidth: 2,
    dataKey: '0',
    name: '0',
    color: '#B17395',
    value: 0,
    payload: {
      0: 0,
      1: 0,
      2: 0,
      3: 3,
      4: 0,
      name: 'Aug 20',
      first: 3,
      last: 3,
    },
  },
  {
    dataKey: '1',
    name: '1',
    value: 0,
    payload: {
      0: 0,
      1: 0,
      2: 0,
      3: 3,
      4: 0,
      name: 'Aug 20',
      first: 3,
      last: 3,
    },
  },
  {
    dataKey: '2',
    name: '2',
    value: 0,
    payload: {
      0: 0,
      1: 0,
      2: 0,
      3: 3,
      4: 0,
      name: 'Aug 20',
      first: 3,
      last: 3,
    },
  },
  {
    dataKey: '3',
    name: '3',
    value: 3,
    payload: {
      0: 0,
      1: 0,
      2: 0,
      3: 3,
      4: 0,
      name: 'Aug 20',
      first: 3,
      last: 3,
    },
  },
  {
    dataKey: '4',
    name: '4',
    value: 0,
    payload: {
      0: 0,
      1: 0,
      2: 0,
      3: 3,
      4: 0,
      name: 'Aug 20',
      first: 3,
      last: 3,
    },
  },
];

export const tooltipDataMock = [
  {
    name: 'Aug 20',
    values: [0],
  },
];

export const unresolvedLocationsInAlarmLimit = 100;

export const unresolvedLocationsInAlarmOffsetFromPage = (page: number) =>
  page * unresolvedLocationsInAlarmLimit;
